/**
 * Mode-aware tracker data hook. Exposes:
 *  - `stageCounts` per tab
 *  - `rowsByStage` matching what `EpisodeTrackerPage` already consumed
 *  - `transition(serviceRequestId, nextStage, options)` that updates the live
 *    resource and (in mock mode) updates local state optimistically.
 *
 * In mock mode, this hook simply proxies the existing `mockLifecycleData`
 * helpers so the tracker UI is bit-for-bit unchanged.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import {
  listTrackerRows,
  transitionServiceRequestStage,
  type StageTransitionOptions,
  type TrackerRowBundle,
} from '../lib/serviceRequestService';
import type {
  LifecycleStage,
} from '../pages/episode-tracker/types/lifecycle';
import {
  TRACKER_STAGE_CODES,
  type TrackerStageCode,
} from '../lib/serviceRequestFhirConstants';
import type { StageRow } from '../pages/episode-tracker/components/StageTab';

export interface UseServiceRequestTrackerReturn {
  isMock: boolean;
  loading: boolean;
  error: string | null;
  stageCounts: Record<LifecycleStage, number>;
  rowsByStage: Record<LifecycleStage, StageRow[]>;
  /** Raw resources for live mode so callers can open detail drawers with context. */
  serviceRequests: ServiceRequest[];
  /** Transition the given SR to a new stage. No-op in mock mode. */
  transition: (
    serviceRequestId: string,
    nextStage: TrackerStageCode,
    options?: StageTransitionOptions,
  ) => Promise<void>;
  /** Re-fetch everything (live mode). */
  refresh: () => void;
}

const EMPTY_COUNTS: Record<LifecycleStage, number> = {
  'new-client': 0,
  'needs-verification': 0,
  waitlist: 0,
  screening: 0,
  matching: 0,
  consultation: 0,
  active: 0,
  transfer: 0,
  inactive: 0,
  cancelled: 0,
  fulfilled: 0,
  all: 0,
};

function emptyRowsByStage(): Record<LifecycleStage, StageRow[]> {
  return {
    'new-client': [],
    'needs-verification': [],
    waitlist: [],
    screening: [],
    matching: [],
    consultation: [],
    active: [],
    transfer: [],
    inactive: [],
    cancelled: [],
    fulfilled: [],
    all: [],
  };
}

export function useServiceRequestTracker(): UseServiceRequestTrackerReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const live = true;

  const [bundle, setBundle] = useState<TrackerRowBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!profile) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    medplum.invalidateSearches('ServiceRequest');
    void (async () => {
      try {
        const result = await listTrackerRows({ live: true, medplum });
        if (!cancelled) setBundle(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load service requests');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [live, medplum, profile, reloadTick]);

  const liveRowsByStage = useMemo((): Record<LifecycleStage, StageRow[]> => {
    const grouped = emptyRowsByStage();
    if (!bundle) return grouped;
    for (const row of bundle.rows) {
      const stage = (row.stage ?? 'all') as LifecycleStage;
      const target = grouped[stage] ?? grouped.all;
      (target as StageRow[]).push(row as unknown as StageRow);
      (grouped.all as StageRow[]).push(row as unknown as StageRow);
    }
    return grouped;
  }, [bundle]);

  const liveStageCounts = useMemo((): Record<LifecycleStage, number> => {
    const counts: Record<LifecycleStage, number> = { ...EMPTY_COUNTS };
    for (const stage of TRACKER_STAGE_CODES) {
      counts[stage as LifecycleStage] = liveRowsByStage[stage as LifecycleStage]?.length ?? 0;
    }
    counts.active = liveRowsByStage.active?.length ?? 0;
    counts.transfer = liveRowsByStage.transfer?.length ?? 0;
    counts.all = liveRowsByStage.all?.length ?? 0;
    return counts;
  }, [liveRowsByStage]);

  const transition = useCallback(
    async (
      serviceRequestId: string,
      nextStage: TrackerStageCode,
      options?: StageTransitionOptions,
    ) => {
      if (!live) return;
      const target = bundle?.serviceRequests.find((s) => s.id === serviceRequestId);
      if (!target) {
        throw new Error(`ServiceRequest ${serviceRequestId} not loaded`);
      }
      await transitionServiceRequestStage({
        live: true,
        medplum,
        serviceRequest: target,
        nextStage,
        options,
      });
      setReloadTick((n) => n + 1);
    },
    [live, medplum, bundle],
  );

  const refresh = useCallback(() => setReloadTick((n) => n + 1), []);

  return {
    isMock: false,
    loading,
    error,
    stageCounts: liveStageCounts,
    rowsByStage: liveRowsByStage,
    serviceRequests: bundle?.serviceRequests ?? [],
    transition,
    refresh,
  };
}
